import { Link } from 'react-router'
import type { Alert } from '@fitting-room/contracts'
import { TriangleAlert } from 'lucide-react'
import { ALERT_KIND_LABEL } from '../../../lib/fulfillment'

/**
 * What needs doing about an order, at the top of its page: each alert's kind
 * and its sentence, critical first, as the API sorted them. Nothing at all
 * when there is nothing — an order with no alerts needs no reassurance box.
 */
export function OrderAlerts({ alerts }: { readonly alerts: readonly Alert[] }) {
  if (alerts.length === 0) return null
  return (
    <section className="st-alert-panel mb-5" aria-label="Alerts on this order" data-testid="order-alerts">
      <ul className="space-y-1.5">
        {alerts.map((alert) => (
          <li key={alert.id} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm" data-alert-kind={alert.kind}>
            <AlertBadge alert={alert} />
            {alert.lineDescription ? <span className="font-medium">{alert.lineDescription}</span> : null}
            <span>{alert.message}</span>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** The kind, in the color of how bad it is. */
export function AlertBadge({ alert }: { readonly alert: Pick<Alert, 'kind' | 'severity'> }) {
  return (
    <span className="st-badge st-alert-kind" data-severity={alert.severity}>
      {alert.severity === 'critical' ? <TriangleAlert size={11} className="mr-1" aria-hidden /> : null}
      {ALERT_KIND_LABEL[alert.kind]}
    </span>
  )
}

/**
 * One alert on the Alerts page, a row you can hit with a thumb: it opens the
 * order it is about, or the appointment for a follow-up — on the follow-up
 * queue, where marking it done is one tap away.
 */
export function AlertRow({ alert }: { readonly alert: Alert }) {
  const to = alert.orderId ? `/admin/orders/${alert.orderId}` : `/admin/follow-ups?visit=${encodeURIComponent(alert.visitId ?? '')}`
  return (
    <Link to={to} className="st-card-action st-row mb-2 block px-4 py-3" data-alert-id={alert.id} data-alert-kind={alert.kind} data-severity={alert.severity}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
          {alert.orderNo ? <span className="st-display shrink-0 text-lg tabular-nums">{alert.orderNo}</span> : null}
          <span className="st-row-name truncate font-medium">{alert.customerName}</span>
          {alert.lineDescription ? <span className="st-meta text-sm">{alert.lineDescription}</span> : null}
        </div>
        <AlertBadge alert={alert} />
      </div>
      <p className="mt-1.5 text-sm">{alert.message}</p>
    </Link>
  )
}
